import { useState, useEffect, ChangeEvent, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { teamsService } from '../../../../../../app/services/teamsService'
import { useToast } from '../../../../../components/ui/use-toast'
import { SellersContext } from '../../../../../../app/contexts/SellerContex'

type useAddMembersProps = {
  _id: string
  name: string
  profileImageUrl: string
}

export const useAddMembers = () => {
  const { id } = useParams()
  const [selectedUsers, setSelectedUsers] = useState<useAddMembersProps[]>([])
  const { ref, inView } = useInView()
  const queryClient = useQueryClient()

  const { sellers, setSearchValue, searchValue } = useContext(SellersContext)

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    sellers
  const { toast } = useToast()

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView])

  const handleAddUserToMemberList = (user: useAddMembersProps) => {
    setSelectedUsers((prev) =>
      prev.includes(user)
        ? prev.filter((selectedUser) => selectedUser._id !== user._id)
        : [...prev, user],
    )
  }

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['AddMember'],
    mutationFn: async ({
      teamId,
      memberId,
    }: {
      teamId: string
      memberId: string
    }) => await teamsService.addTeamMember(teamId, memberId),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['teamId', id],
      })
      queryClient.resetQueries({
        queryKey: ['Allsellers', { teamIds: id }],
      })
    },
  })

  const handleSendMembersList = async () => {
    try {
      if (selectedUsers.length > 0) {
        await Promise.all(
          selectedUsers.map(async (selectedUser) => {
            return await mutateAsync({
              teamId: id!,
              memberId: selectedUser._id,
            })
          }),
        )

        toast({
          title: 'Membros adicionados com sucesso.',
        })
      }

      setSelectedUsers([])
    } catch (error) {
      console.log(error)
      toast({
        variant: 'destructive',
        title: 'Erro ao adicionar os membros, tente novamente mais tarde',
      })
    }
  }

  const handleResetMemberList = () => {
    setSelectedUsers([])
  }

  return {
    data,
    isFetching,
    searchValue,
    handleSearchInputChange,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ref,
    handleAddUserToMemberList,
    handleSendMembersList,
    selectedUsers,
    isPending,
    handleResetMemberList,
  }
}
